import { useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { AppDialog } from '../../../components/AppDialog';
import { useConfirm } from '../../../components/confirm';
import { useNotify } from '../../../components/notifications';
import {
  PC_HINT, allTotals, buildGrid, cellKey, draftFromSlides, exactlyOnePercentRefusal,
  fmtPercentDisplay, pointsPerSlide, slideCount, slidesFromDraft, traceCount,
  validatePointCount,
  type PlmPointCountSlide, type PointCountDraft, type PointCountType,
} from './plmPointCount';

/**
 * "PC Entry — Point Count" — the 400 / 1000-point slide count (PLM Phase 2),
 * laid out as prototype v8's `.pcdlg` (redesigned 2026-09-26):
 *
 *   Slide │ fiber₁ AP · Trace │ fiber₂ AP · Trace │ … │ NAP
 *   S1    │ [  ]      [ ]     │ [  ]      [ ]     │   │ 50
 *   …
 *   Total │ Σ         n       │ Σ         n       │   │ Σ
 *   %AP   │ 8%                │ <0.25%            │   │
 *
 * ONE NAP column per SLIDE — the points left after EVERY fiber's AP on that
 * slide — not one per fiber. That is the physical reading (a slide has 50 or
 * 100 points, full stop) and it is what lets the grid flag a slide whose fibers
 * together exceed it. The stored %AP per fiber is unchanged: AP + NAP per fiber
 * always sums to the whole count, so the per-fiber result is AP / total either way.
 *
 * The analyst types only AP and ticks Trace; NAP, the totals and %AP are
 * derived live here and re-derived authoritatively by the server on save.
 *
 * Built on the shared <AppDialog> (Esc, Ctrl+S, focus in / focus back out —
 * standing rule 8) and themed through its `className` seam to the prototype's
 * white header + meta line, the way Sample Registration themes `.sr-dlg`.
 */

export interface PlmPointCountDialogProps {
  open: boolean;
  /** The asbestos fibers on this layer, in exam-grid order. */
  fiberNames: string[];
  pointCountType: PointCountType;
  /** Whatever was counted before — carried over per fiber and slide. */
  existing: PlmPointCountSlide[];
  /** The layer's SysSampleCode, for the header's meta line. */
  layerId?: string | null;
  onCancel: () => void;
  onSave: (slides: PlmPointCountSlide[]) => void | Promise<void>;
}

export function PlmPointCountDialog({
  open, fiberNames, pointCountType, existing, layerId, onCancel, onSave,
}: PlmPointCountDialogProps) {
  const notify = useNotify();
  const { confirm } = useConfirm();

  // Seeded once per opening from the LIVE fiber list, carrying over the counts
  // of fibers that survived (legacy PCEntry_Execute :121).
  const [draft, setDraft] = useState<PointCountDraft>(
    () => draftFromSlides(buildGrid(fiberNames, existing, pointCountType)),
  );
  // While the Clear confirm is up, Esc must dismiss THAT, not this dialog.
  const [confirming, setConfirming] = useState(false);

  const per  = pointsPerSlide(pointCountType);
  const rows = slideCount(pointCountType);

  const slides  = useMemo(() => slidesFromDraft(fiberNames, draft, pointCountType), [fiberNames, draft, pointCountType]);
  const totals  = useMemo(() => allTotals(slides, pointCountType), [slides, pointCountType]);
  const check   = useMemo(() => validatePointCount(fiberNames, draft, pointCountType), [fiberNames, draft, pointCountType]);
  const refusal = useMemo(() => exactlyOnePercentRefusal(slides, pointCountType), [slides, pointCountType]);

  const totalsFor = (fiberName: string) => totals.find(t => t.fiberName === fiberName);
  const napTotal  = check.slideNap.reduce((a, b) => a + b, 0);

  const setRaw   = (k: string, text: string) => setDraft(d => ({ ...d, raw:   { ...d.raw,   [k]: text } }));
  const setTrace = (k: string, on: boolean)  => setDraft(d => ({ ...d, trace: { ...d.trace, [k]: on } }));

  const gridRef = useRef<HTMLTableElement>(null);

  /**
   * Grid keys (rule 14; prototype v8): a point count is read DOWN a fiber's
   * column at the microscope, so Enter / ↓ go to the next slide of the same
   * fiber, ↑ / Shift+Enter to the previous, and ← / → to the neighbouring
   * fiber once the caret is at the edge of the text. The dialog's own
   * Enter-to-submit is switched off so Enter can never fire Apply mid-count.
   */
  const onGridKeyDown = (e: ReactKeyboardEvent<HTMLInputElement>, col: number, slideNo: number) => {
    let nextCol = col;
    let nextSlide = slideNo;
    if (e.key === 'Enter')          nextSlide = slideNo + (e.shiftKey ? -1 : 1);
    else if (e.key === 'ArrowDown') nextSlide = slideNo + 1;
    else if (e.key === 'ArrowUp')   nextSlide = slideNo - 1;
    else if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      const el = e.currentTarget;
      const atEnd   = el.selectionStart === el.value.length && el.selectionEnd === el.value.length;
      const atStart = el.selectionStart === 0 && el.selectionEnd === 0;
      if (e.key === 'ArrowRight' && !atEnd) return;
      if (e.key === 'ArrowLeft' && !atStart) return;
      nextCol = col + (e.key === 'ArrowRight' ? 1 : -1);
    } else return;

    if (e.key === 'Enter') e.preventDefault();          // never submits, even at the last row
    if (nextSlide < 1 || nextSlide > rows || nextCol < 0 || nextCol >= fiberNames.length) return;
    const target = gridRef.current?.querySelector<HTMLInputElement>(
      `input[data-pc-col="${nextCol}"][data-pc-slide="${nextSlide}"]`);
    if (!target) return;
    e.preventDefault();
    target.focus();
    target.select();
  };

  const clear = async () => {
    setConfirming(true);
    try {
      const ok = await confirm({
        title: 'Clear counts',
        message: 'Clear every AP and Trace entry in this point count?',
        tone: 'danger',
      });
      if (ok) setDraft({ raw: {}, trace: {} });
    } finally {
      setConfirming(false);
    }
  };

  const apply = async () => {
    if (!check.canApply) return;
    if (refusal) { notify.warning(refusal); return; }
    await onSave(slides);
  };

  const meta = `${layerId ? `${layerId} · ` : ''}${pointCountType} points = ${rows} slides × ${per} points`;

  return (
    <AppDialog
      open={open}
      className="plm-pcdlg"
      title={<>PC Entry — Point Count<span className="plm-pc__meta">{meta}</span></>}
      onClose={onCancel}
      closeOnEsc={!confirming}
      submitOnEnter={false}
      width={760}
      footer={
        <>
          <span className={`plm-pc__msg${check.canApply ? '' : ' is-bad'}`} role="status">
            {fiberNames.length ? check.message : PC_HINT}
          </span>
          <button type="button" className="ca-btn" onClick={() => { void clear(); }}
                  disabled={fiberNames.length === 0}>Clear</button>
          <button type="button" className="ca-btn" onClick={onCancel}>Cancel</button>
          <button type="button" className="ca-btn ca-btn-solid"
                  disabled={fiberNames.length === 0 || !check.canApply}
                  onClick={() => { void apply(); }}>Apply</button>
        </>
      }
    >
      {fiberNames.length === 0 ? (
        <div className="plm-exam__empty">
          Add an <b>Asbestos Fibers</b> component in the grid first — each one gets its
          own AP column in the point count.
        </div>
      ) : (
        <div className="plm-pc">
          {refusal && <div className="plm-pc__refusal" role="alert">{refusal}</div>}

          <div className="plm-pc__scroll">
            <table className="plm-pc__table" ref={gridRef}>
              <thead>
                <tr>
                  <th rowSpan={2}>Slide</th>
                  {fiberNames.map(name => <th key={name} colSpan={2}>{name}</th>)}
                  <th rowSpan={2}>NAP</th>
                </tr>
                <tr>
                  {fiberNames.map(name => [
                    <th key={`${name}-ap`}>AP</th>,
                    <th key={`${name}-tr`}>Trace</th>,
                  ])}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: rows }, (_, i) => i + 1).map(slideNo => {
                  const nap = check.slideNap[slideNo - 1] ?? per;
                  return (
                    <tr key={slideNo}>
                      <th scope="row" className="plm-pc__sl">S{slideNo}</th>
                      {fiberNames.map((name, col) => {
                        const k = cellKey(name, slideNo);
                        const bad = check.badCells.has(k);
                        return [
                          <td key={`${k}-ap`}>
                            <input
                              type="text"
                              inputMode="numeric"
                              className={`plm-pcin${bad ? ' is-bad' : ''}`}
                              placeholder="0"
                              value={draft.raw[k] ?? ''}
                              data-pc-col={col}
                              data-pc-slide={slideNo}
                              aria-label={`${name} slide ${slideNo} asbestos points`}
                              aria-invalid={bad || undefined}
                              onChange={e => setRaw(k, e.target.value)}
                              onKeyDown={e => onGridKeyDown(e, col, slideNo)}
                            />
                          </td>,
                          <td key={`${k}-tr`} className="plm-pc__cb">
                            <input
                              type="checkbox"
                              checked={draft.trace[k] === true}
                              aria-label={`${name} slide ${slideNo} trace`}
                              onChange={e => setTrace(k, e.target.checked)}
                            />
                          </td>,
                        ];
                      })}
                      <td className={`plm-pc__nap${nap < 0 ? ' is-bad' : ''}`}
                          aria-label={`Slide ${slideNo} non-asbestos points`}>{nap}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <th scope="row">Total</th>
                  {fiberNames.map(name => {
                    const n = traceCount(name, draft, pointCountType);
                    return [
                      <td key={`${name}-tap`} aria-label={`${name} total asbestos points`}>
                        {totalsFor(name)?.totalAp ?? 0}
                      </td>,
                      <td key={`${name}-trn`} className="plm-pc__trn"
                          aria-label={`${name} trace count`}>{n || ''}</td>,
                    ];
                  })}
                  <td aria-label="Total non-asbestos points">{napTotal}</td>
                </tr>
                <tr className="plm-pc__pct">
                  <th scope="row">%AP</th>
                  {fiberNames.map(name => {
                    const t = totalsFor(name);
                    return (
                      <td key={`${name}-pct`} colSpan={2} aria-label={`${name} percent asbestos`}>
                        {t ? fmtPercentDisplay(t) : ''}
                      </td>
                    );
                  })}
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </AppDialog>
  );
}
